import React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import {
  Table, Row, Col, Input, Modal, Button, Space, Checkbox, message, Select
} from 'antd';
import { LockOutlined, PlusOutlined } from '@ant-design/icons';
import * as api from 'app/shared/api';
import {
  filterData,
  getFeeProductById,
  listFeesProductsData,
  resetFeeModal,
  setFormErrors
} from '../actions';
import {
  FilterValueType,
  ProductFilter,
  ProductKey,
  Product
} from 'app/shared/model/ProductBase';
import { getFeesData, getFeesProductsData, getUnitTypes } from '../selectors';
import { Fee } from 'app/shared/model/Fee';
import DateHelper from 'app/helpers/dateHelper';
import { UnitType } from 'app/shared/model/UnitType';
import filterHelper from 'app/helpers/filterHelper';
import TableSearch from 'app/shared/components/Table/TableSearch';
import TableActionButtons from 'app/shared/components/Table/TableActionButtons';
import FilterResetLink from 'app/shared/components/Table/FilterResetLink';
import TablePagination from 'app/shared/components/Table/TablePagination';
import AddEditFeeForm from './AddEditFeeForm';
import { useSignalREvent, PublishedEventType, StatusIcon } from 'app/helpers/signalRHelper';
import { sortProductsWithStatus, useProductGridSorting } from './productGridSorting';
import ProductStatusFilter from './ProductStatusFilter';
import { ProductStatusFilterValue } from './productStatusFilterUtils';
import { buildGridFilteredList } from './productGridFilterPipeline';
import { useAutoOpenProductModal } from '../hooks/useAutoOpenProductModal';
import { readHasActiveOffering, readIsUsedInActivePackage } from 'app/shared/api/productDtoHelpers';

const mapApiFeeToGrid = (feeApi: any): Fee => {
  const unitTypeId = Number(feeApi.unitTypeId ?? feeApi.unitType?.id ?? 0);
  const unitTypeName = feeApi.unitTypeName || feeApi.unitType?.name || '';
  return {
  id: Number(feeApi.id),
  name: feeApi.name || '',
  unitTypeName,
  unitTypeId,
  active: feeApi.active,
  lastModifiedDate: feeApi.lastModifiedDate ?? null,
  lastUpdatedBy: feeApi.lastUpdatedBy ?? feeApi.lastModifiedBy ?? '',
  entityUpdate: Boolean(feeApi.entityUpdate),
  excludedBookingSystemIds: Array.isArray(feeApi.excludedBookingSystemIds)
    ? feeApi.excludedBookingSystemIds.map((id: number | string) => String(id))
    : [],
  hasActiveOffering: readHasActiveOffering(feeApi),
  isUsedInActivePackage: readIsUsedInActivePackage(feeApi),
}};

export interface FeesGridProps {}

interface StateProps {
  filteredData?: Fee[];
  feeProducts?: Fee[];
  unitTypes?: UnitType[];
}

interface DispatchProps {
  onCancel: () => Promise<Fee[]>;
  onFilterData: (filteredData: Fee[] | undefined) => void;
  onSelectedFeeData: (feeProduct: Fee | undefined | void) => void;
}

type InternalProps = StateProps & DispatchProps & FeesGridProps;

function FeesGrid(props: InternalProps) {
  const [showFilters, setShowFilters] = React.useState(false);
  const [filters, setFilters] = React.useState<ProductFilter[]>([]);
  const [statusFilter, setStatusFilter] = React.useState<ProductStatusFilterValue>('all');
  const [isSearchActive, setIsSearchActive] = React.useState(false);
  const [searchFilteredData, setSearchFilteredData] = React.useState<Fee[] | undefined>(undefined);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [itemsPerPage, setItemsPerPage] = React.useState(10);
  const { sortKey, sortOrder, getColumnSortProps, handleSorterChange } = useProductGridSorting();
  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalFeeId, setModalFeeId] = React.useState<number | null>(null);

  const productList = props.feeProducts ?? [];

  const publishFilteredData = React.useCallback((
    nextFilters = filters,
    nextShowFilters = showFilters,
    nextStatus = statusFilter,
    nextSearchActive = isSearchActive,
    nextSearchData = searchFilteredData,
  ) => {
    props.onFilterData(buildGridFilteredList(productList, {
      showFilters: nextShowFilters,
      filters: nextFilters,
      isSearchActive: nextSearchActive,
      searchFilteredData: nextSearchData,
      statusFilter: nextStatus,
    }));
  }, [filters, showFilters, statusFilter, isSearchActive, searchFilteredData, productList, props.onFilterData]);
  const unitTypeLookup = React.useMemo(() => {
    const map = new Map<number, string>();
    (props.unitTypes ?? []).forEach(u => map.set(Number(u.id), u.name));
    return map;
  }, [props.unitTypes]);

  const getFeeProduct = (id: number) => {
    const fee = (props.feeProducts ?? []).find(f => f.id === id);
    props.onSelectedFeeData(fee);
    setModalFeeId(id);
    setModalOpen(true);
  };

  useAutoOpenProductModal('Fees', (id) => {
    const fee = (props.feeProducts ?? []).find(f => f.id === id);
    if (fee) {
      getFeeProduct(id);
      return;
    }
    void api.getFeeById(id.toString()).then((feeApi) => {
      if (!feeApi) {
        return;
      }
      props.onSelectedFeeData(feeApi);
      setModalFeeId(id);
      setModalOpen(true);
    });
  });

  const refreshGridEvent = async () => {
    await api.getAllFees();
    publishFilteredData(filters, showFilters, statusFilter, isSearchActive, searchFilteredData);
  }

  // Subscribe to event and run refreshGridEvent when triggered
  const { status, lastEventReceived } = useSignalREvent([PublishedEventType.FeeEventPublished], refreshGridEvent);

  const handleClearFilters = () => {
    setFilters([]);
    setCurrentPage(1);
    publishFilteredData([], showFilters, statusFilter, isSearchActive, searchFilteredData);
  };

  const handleFilterChange = (
    value: any,
    field: keyof ProductKey,
    valueType: FilterValueType = FilterValueType.primaryValue
  ) => {
    const newFilters = filterHelper.getFilters(filters, value, field, valueType);
    setFilters(newFilters);
    publishFilteredData(newFilters);
    setCurrentPage(1);
  };

  const getUniqueUnitTypeFilter = (list: Fee[]) => {
    const data = list.map(d => ({ id: d.unitTypeName || unitTypeLookup.get(Number(d.unitTypeId)) || '', name: d.unitTypeName || unitTypeLookup.get(Number(d.unitTypeId)) || '' }));
    const unique = data.filter((v, i, a) => v.id && a.findIndex(x => x.id === v.id) === i);
    return unique.map(v => ({ label: v.name, value: v.id }));
  };

  const filteredData = props.filteredData ?? productList;
  const sortedData = React.useMemo(() => {
    return sortProductsWithStatus(filteredData as Fee[], sortKey, sortOrder, statusFilter);
  }, [filteredData, sortKey, sortOrder, statusFilter]);
  const pagedData = TablePagination.dataPaging(sortedData as any[], currentPage, itemsPerPage);
  const tableData = (pagedData as Fee[]).map(item => ({
    ...item,
    unitTypeName: item.unitTypeName || unitTypeLookup.get(Number(item.unitTypeId)) || '',
  }));
  const totalItems = sortedData.length;

  const columns = [
    {
      title: 'Id',
      dataIndex: 'id',
      key: 'id',
      width: 30,
      render: (id: number, rec: Fee) => (
        <>
          {id}
          {rec.entityUpdate && <LockOutlined style={{ marginLeft: 4, color: '#8c8c8c' }} />}
        </>
      )
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ...getColumnSortProps('name'),
      render: (text: string, record: Fee) =>
        record.entityUpdate ? (
          <span style={{ color: 'rgba(0,0,0,0.65)' }}>{text} (Publishing an update)</span>
        ) : (
          <a href="#0" onClick={e => { e.preventDefault(); getFeeProduct(record.id); }}>{text}</a>
        )
    },
    {
      title: 'Unit Type',
      dataIndex: 'unitTypeName',
      key: 'unitTypeName',
      width: 120,
      render: (_: string, record: Fee) => record.unitTypeName || unitTypeLookup.get(Number(record.unitTypeId)) || '',
    },
    {
      title: 'Last Modified Date',
      dataIndex: 'lastModifiedDate',
      key: 'lastModifiedDate',
      width: 160,
      ...getColumnSortProps('lastModifiedDate'),
      render: (value: string | null) => DateHelper.ToDateString(value ?? undefined)
    },
    {
      title: 'Last Updated By',
      dataIndex: 'lastUpdatedBy',
      key: 'lastUpdatedBy',
      width: 150,
      ...getColumnSortProps('lastUpdatedBy'),
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      width: 40,
      render: (val: boolean) => val ? <span style={{ color: 'green' }}>Yes</span> : <span style={{ color: 'red' }}>No</span>

    }

  ];

  const handleFeeSaved = React.useCallback(async () => {
    try {
      const latest = await props.onCancel();
      const latestList = Array.isArray(latest) ? latest : (props.feeProducts ?? []);
      if (filters.length) {
        publishFilteredData(filters, showFilters, statusFilter, isSearchActive, searchFilteredData);
      } else {
        publishFilteredData([], showFilters, statusFilter, isSearchActive, searchFilteredData);
      }
    } catch (error) {
      console.error('Failed to refresh fees after save', error);
      message.warning('Fee saved, but we could not refresh the list. Please refresh the page.');
    } finally {
      setCurrentPage(1);
      setModalFeeId(null);
      setModalOpen(false);
    }
  }, [filters, showFilters, statusFilter, isSearchActive, searchFilteredData, props.feeProducts, props.onCancel, publishFilteredData]);

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 12 }}>
        <Col flex={2}>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <TableSearch
                disabled={showFilters}
                dataList={productList as unknown as Product[]}
                onSearch={(data, searchActive = false) => {
                  setCurrentPage(1);
                  setIsSearchActive(searchActive);
                  setSearchFilteredData(data as unknown as Fee[]);
                  publishFilteredData(filters, showFilters, statusFilter, searchActive, data as unknown as Fee[]);
                }}
              />
            </div>
            <ProductStatusFilter
              value={statusFilter}
              onChange={(nextStatus) => {
                setStatusFilter(nextStatus);
                setCurrentPage(1);
                publishFilteredData(filters, showFilters, nextStatus, isSearchActive, searchFilteredData);
              }}
            />
          </div>
        </Col>
        <Col flex={3} style={{ textAlign: 'right' }}>
          <Space>
            <StatusIcon status={status} lastEventReceived={lastEventReceived} unpublishedChanges={Array.isArray(filteredData) && filteredData.some(x => x?.entityUpdate === true)} onManualRefresh={refreshGridEvent}/>
            <TableActionButtons
              showFilters={showFilters}
              dataList={filteredData}
              onShowFiltersClick={() => {
                const nextShowFilters = !showFilters;
                setShowFilters(nextShowFilters);
                if (nextShowFilters) {
                  setFilters([]);
                  publishFilteredData([], true, statusFilter, isSearchActive, searchFilteredData);
                } else {
                  publishFilteredData(filters, false, statusFilter, isSearchActive, searchFilteredData);
                }
                setCurrentPage(1);
              }}
              canFilter
              canExport
              canEdit={false}
              canAdd={false}
              csvFileName={`${DateHelper.ToDateString(new Date(), 'yyyy.MM.dd')}_Fees_Export`}
            />
          </Space>
        </Col>
        <Col>
          <Button
            type="primary"
            onClick={() => { setModalFeeId(null); setModalOpen(true); }}
            icon={<PlusOutlined />}
          >
            Add a new Fee
          </Button>
        </Col>
      </Row>

      {showFilters && (
        <Row gutter={12} style={{ marginBottom: 12 }}>
          <Col>
            <Input
              placeholder="Search..."
              value={filterHelper.getFieldValue(filters, 'name', '')}
              onChange={e => handleFilterChange(e.target.value, 'name')}
              allowClear
              style={{ width: 180 }}
            />
          </Col>
          <Col>
            <Select
              mode="multiple"
              allowClear
              placeholder="Unit Type"
              value={filterHelper.getFieldValue(filters, 'unitTypeName' as keyof ProductKey)}
              onChange={(vals: any) => handleFilterChange(vals, 'unitTypeName' as keyof ProductKey)}
              options={getUniqueUnitTypeFilter(productList)}
              style={{ width: 160 }}
            />
          </Col>
          <Col style={{ display: 'flex', alignItems: 'center' }}>
            <FilterResetLink onClick={handleClearFilters} />
          </Col>
        </Row>
      )}

      <Table
        columns={columns}
        dataSource={tableData}
        rowKey="id"
        pagination={false}
        bordered
        size="middle"
        style={{ marginBottom: 12 }}
        showSorterTooltip={false}
        onChange={(_, __, sorter: any, extra: any) => {
          handleSorterChange(sorter, extra?.action);
        }}
      />
      <TablePagination.Paging
        noOfItems={totalItems}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        onItemsPerPageChange={(value, page) => { setItemsPerPage(value); setCurrentPage(page); }}
        onPageChange={page => setCurrentPage(page)}
        showItemsPerPage
      />

      <Modal
        title={modalFeeId ? 'Update Fee' : 'Add Fee'}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setModalFeeId(null); }}
        footer={null}
        style={{ top: 50 }}
        width={980}
        destroyOnHidden
      >
        <AddEditFeeForm
          key={modalFeeId ?? 'new'}
          editMode={modalFeeId != null}
          feeProductId={modalFeeId ?? undefined}
          onSuccess={handleFeeSaved}
        />
      </Modal>
    </div>
  );
}

const mapStateToProps = (state: any): StateProps => ({
    feeProducts: getFeesProductsData(state),
    filteredData: getFeesData(state),
    unitTypes: getUnitTypes(state) || [],
});

const mapDispatchToProps = (dispatch: Dispatch<any>): DispatchProps => ({
  onFilterData: (filteredData) => dispatch(filterData(filteredData)),
  onSelectedFeeData: feeProduct => dispatch(getFeeProductById(feeProduct!)),
  onCancel: () => {
    dispatch(setFormErrors([]));
    return api.getAllFees().then(feesApi => {
      const data = Array.isArray(feesApi) ? feesApi : [feesApi];
      const mapped = data.map(mapApiFeeToGrid);
      dispatch(resetFeeModal());
      dispatch(listFeesProductsData(mapped));
      return mapped;
    });
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(FeesGrid);